import React, { useCallback, useEffect, useRef, useState } from "react";
import { connectSocket } from "../socket";
import { authFetch } from "../utils/auth";
import SideBar from "./SideBar";
import { useLocation, useNavigate } from "react-router-dom";

const AgentDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  const [escalatedSessions, setEscalatedSessions] = useState([]);
  const [pendingActions, setPendingActions] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  // "waiting" = the queue an engineer can act on. "rejected" = the ones that
  // never reached anyone: declined, the customer gone, or nobody accepted in
  // time. Two reads of the same table, so a row moves between the views by
  // itself — nothing has to write to it.
  const [view, setView] = useState("waiting");
  // The socket effect registers its handlers once. Without these refs those
  // closures keep the view (and the fetch) they were created with, and the
  // Rejected tab would go on behaving like the Waiting one.
  const viewRef = useRef("waiting");
  // The wait window is the server's to decide; the label just reports it.
  const [waitTimeoutS, setWaitTimeoutS] = useState(300);
  const refreshQueueRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const theme = {
    dark: {
      bg: "bg-black",
      cardBg: "bg-gray-950",
      text: "text-white",
      textSecondary: "text-gray-300",
      textMuted: "text-gray-400",
      timestampColor: "text-gray-400",
      border: "border-gray-800",
      hover: "hover:bg-gray-900",
      tableHeader: "bg-gray-900",
      rowHover: "hover:bg-gray-900/60",
    },
    light: {
      bg: "bg-[#fafaf5]",
      cardBg: "bg-white",
      text: "text-gray-900",
      textSecondary: "text-gray-600",
      textMuted: "text-gray-500",
      timestampColor: "text-gray-600",
      border: "border-[#f6f6f0]",
      hover: "hover:bg-gray-50",
      tableHeader: "bg-[#f6f6f0]",
      rowHover: "hover:bg-gray-50",
    },
  };

  const currentTheme = isDarkMode ? theme.dark : theme.light;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  const escalatedSessionLabel = (sess) => {
    const raw = (sess?.userInfo?.name || "").trim();
    const lower = raw.toLowerCase();
    if (raw && !["unknown", "user", "agent", "tse agent", "customer"].includes(lower)) {
      return raw;
    }
    return sess?.sessionId ? sess.sessionId.slice(-8) : "Session";
  };

  const formatFirstPrompt = (sess) => {
    const t = String(sess.firstPrompt || sess.issueName || "").replace(/\s+/g, " ").trim();
    return t || "—";
  };

  // Why this customer never reached anyone. Three reasons, kept apart because
  // they are three different things to answer for: an engineer turned them
  // down, the customer had already left, or nobody got to them in time.
  const rejectionLabel = (sess) => {
    const who = (sess.declinedBy || "").trim();
    if (sess.reason === "declined") return who ? `Declined by ${who}` : "Declined";
    if (sess.reason === "user_unavailable") return "Customer left";
    return `No reply in ${Math.round((waitTimeoutS || 300) / 60)} min`;
  };

  const handleAccept = (sessionId) => {
    window.open(`/TseDashboard5/new?customer=${encodeURIComponent(sessionId)}`, "_blank");
    setEscalatedSessions((prev) =>
      prev.map((sess) =>
        sess.sessionId === sessionId ? { ...sess, status: "joined" } : sess,
      ),
    );
  };

  const handleDecline = (sessionId) => {
    if (!socket || pendingActions[sessionId]) return;
    setPendingActions((prev) => ({ ...prev, [sessionId]: "decline" }));
    socket.emit("tse:decline_escalation", { customer_session_id: sessionId });
    setEscalatedSessions((prev) =>
      prev.map((sess) =>
        sess.sessionId === sessionId ? { ...sess, status: "declined" } : sess,
      ),
    );
  };

  // The queue, read from the database rather than assembled from whatever
  // socket events this tab happened to receive.
  //
  // It REPLACES the list instead of merging into it, and that is the whole
  // point: merging can only ever add rows, so an escalation that was accepted
  // or declined while this tab's socket was down stayed on screen until
  // someone reloaded — and clicking it produced a refusal with no explanation.
  // Replacing makes every refresh self-healing.
  const refreshQueue = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsRefreshing(true);
    try {
      const res = await authFetch(
        `/api/escalations/pending?view=${encodeURIComponent(view)}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data?.escalations) ? data.escalations : [];
      setEscalatedSessions(
        rows
          .filter((row) => row?.sessionId)
          .map((row) => ({
            ...row,
            escalationTime: row.escalationTime || Date.now(),
            firstPrompt: row.firstPrompt || row.issueName || "",
            sentiment: row.sentiment ?? null,
            // Rejected rows carry their own reason; waiting rows are pending
            // by definition.
            status: row.reason || "pending",
          })),
      );
      // Optimistic per-row state describes rows from the PREVIOUS list. Kept
      // alongside a replaced list it would grey out a button on a row that is
      // not the one it was set for.
      setPendingActions({});
      if (Number.isFinite(data?.wait_timeout_s)) setWaitTimeoutS(data.wait_timeout_s);
      setRefreshError("");
    } catch (err) {
      // The socket stream is still live, so a failed refresh is not an empty
      // queue — say so rather than blanking the table.
      setRefreshError("Could not refresh the queue.");
      console.error("Failed to refresh escalation queue:", err);
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  }, [view]);

  useEffect(() => {
    viewRef.current = view;
    refreshQueueRef.current = refreshQueue;
  }, [view, refreshQueue]);

  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    const onConnect = () => {
      s.emit("joinRoom", { sessionId: "agents", role: "agent" });
      // Reconcile on every (re)connect. This is the moment a tab is most
      // likely to be wrong: anything broadcast while it was away was missed,
      // and the socket replay can only tell it what to ADD.
      refreshQueue({ silent: true });
    };

    const onEscalationAlert = (data) => {
      if (!data?.sessionId) return;
      // A new escalation is by definition waiting. Pushing it into the
      // Rejected view would show a live customer under "never got a reply".
      if (viewRef.current !== "waiting") return;
      const row = {
        ...data,
        escalationTime: data.escalationTime || Date.now(),
        firstPrompt: data.firstPrompt || data.issueName || "",
        sentiment: data.sentiment ?? null,
        status: "pending",
      };
      // A repeat alert for a session already listed is a NEW wait (the same
      // conversation escalated again), so it replaces the old row rather than
      // being dropped — the old one may be greyed out as Opened / Declining.
      setEscalatedSessions((prev) =>
        prev.some((sess) => sess.sessionId === data.sessionId)
          ? prev.map((sess) => (sess.sessionId === data.sessionId ? row : sess))
          : [...prev, row],
      );
      setPendingActions((prev) => {
        if (!prev[data.sessionId]) return prev;
        const next = { ...prev };
        delete next[data.sessionId];
        return next;
      });
    };

    const onEscalationDismissed = (data) => {
      const sessionId = data?.sessionId;
      if (!sessionId) return;
      setEscalatedSessions((prev) => prev.filter((sess) => sess.sessionId !== sessionId));
      // Declined / customer-gone means the row has just BECOME a rejected one.
      // Re-read rather than guess at its reason and timestamps.
      if (viewRef.current !== "waiting") refreshQueueRef.current?.({ silent: true });
      setPendingActions((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    };

    s.on("connect", onConnect);
    s.on("escalationAlert", onEscalationAlert);
    s.on("escalationDismissed", onEscalationDismissed);
    if (s.connected) onConnect();

    return () => {
      s.off("connect", onConnect);
      s.off("escalationAlert", onEscalationAlert);
      s.off("escalationDismissed", onEscalationDismissed);
    };
  }, [refreshQueue]);

  // First paint comes from the database, not from waiting for an event that
  // may already have been sent to somebody else.
  useEffect(() => {
    refreshQueue({ silent: true });
  }, [refreshQueue]);

  return (
    <div className={`flex min-h-screen h-screen w-full ${currentTheme.bg} overflow-hidden transition-colors duration-300`}>
      <SideBar
        mode="agent"
        onLogout={onLogout}
        isDarkMode={isDarkMode}
        onToggleTheme={() => setIsDarkMode((v) => !v)}
        onLinkClick={(path) => navigate(path)}
        currentPath={location.pathname}
      />

      <div className="flex-1 flex flex-col min-h-0 p-6">
        <header className="mb-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <h1 className={`text-xl font-bold ${currentTheme.text}`}>Escalation Queue</h1>
            <div className={`inline-flex rounded-md border ${currentTheme.border} overflow-hidden`}>
              {[
                ["waiting", "Waiting"],
                ["rejected", "Rejected"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    view === key
                      ? "bg-cyan-500 text-white"
                      : `${currentTheme.textMuted} hover:bg-black/5 dark:hover:bg-white/10`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {refreshError && (
              <span className="text-xs text-red-500">{refreshError}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => refreshQueue()}
            disabled={isRefreshing}
            title="Re-read the queue from the server"
            className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors
              ${currentTheme.border} ${currentTheme.text}
              ${isRefreshing ? "opacity-60 cursor-not-allowed" : "hover:bg-black/5 dark:hover:bg-white/10"}`}
          >
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </header>

        <div className={`flex-1 min-h-0 rounded-lg border ${currentTheme.border} ${currentTheme.cardBg} overflow-hidden`}>
          <div className="overflow-auto h-full">
            <table className="w-full text-sm">
              <thead className={`sticky top-0 z-10 ${currentTheme.tableHeader}`}>
                <tr className={`border-b ${currentTheme.border}`}>
                  <th className={`text-left px-4 py-3 font-semibold ${currentTheme.text}`}>User</th>
                  <th className={`text-left px-4 py-3 font-semibold ${currentTheme.text}`}>Time</th>
                  <th className={`text-left px-4 py-3 font-semibold ${currentTheme.text}`}>First prompt</th>
                  <th className={`text-right px-4 py-3 font-semibold ${currentTheme.text}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {escalatedSessions.length === 0 && (
                  <tr>
                    <td colSpan={4} className={`px-4 py-12 text-center ${currentTheme.textMuted}`}>
                      {view === "rejected"
                        ? "Nothing here. Every customer who asked for help reached someone."
                        : "No escalations yet. Waiting for customers to request live support…"}
                    </td>
                  </tr>
                )}
                {escalatedSessions.map((sess) => {
                  const isPending = pendingActions[sess.sessionId];
                  const isActedOn = sess.status === "joined" || sess.status === "declined";
                  return (
                    <tr
                      key={sess.sessionId}
                      className={`border-b ${currentTheme.border} ${currentTheme.rowHover} transition-colors`}
                    >
                      <td className={`px-4 py-3 font-medium ${currentTheme.text}`}>
                        {escalatedSessionLabel(sess)}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap ${currentTheme.timestampColor}`}>
                        {formatTimestamp(sess.escalationTime)}
                      </td>
                      <td className={`px-4 py-3 whitespace-normal break-words ${currentTheme.textSecondary}`}>
                        {formatFirstPrompt(sess)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {view === "rejected" ? (
                          <span className={`text-xs ${currentTheme.textMuted}`}>
                            {rejectionLabel(sess)}
                          </span>
                        ) : (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleAccept(sess.sessionId)}
                            disabled={Boolean(isPending) || isActedOn}
                            className="px-3 py-1.5 rounded-md text-xs font-medium bg-[#06b6d4] text-white hover:bg-[#0891b2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {sess.status === "joined" ? "Opened" : "Accept"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDecline(sess.sessionId)}
                            disabled={Boolean(isPending) || isActedOn}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${currentTheme.border} ${currentTheme.textSecondary} ${currentTheme.hover} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                          >
                            {sess.status === "declined" || isPending === "decline" ? "Declining…" : "Decline"}
                          </button>
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
